import { FileText, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatBytes, type PendingAttachment } from '@/lib/assistant-attachments';

interface AttachmentThumbnailsProps {
  attachments: PendingAttachment[];
  /** Quantidade de arquivos ainda sendo processados — mostra placeholder com spinner. */
  processingCount?: number;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

/** Extensao em maiuscula para o rotulo do card de documento (ex: PDF, DOCX). */
function extensionLabel(name: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(name.trim());
  return match ? match[1].toUpperCase() : 'ARQUIVO';
}

/**
 * Preview dos anexos antes de enviar.
 *
 * IMAGEM (rodada 2): miniatura real da imagem ja redimensionada.
 * DOCUMENTO (rodada 3): icone + nome do arquivo + tamanho. Nao ha miniatura
 * porque nao ha o que renderizar no client — o resize em canvas so vale para
 * imagem, e rasterizar um PDF no browser so para gerar preview nao se paga.
 */
export function AttachmentThumbnails({
  attachments,
  processingCount = 0,
  onRemove,
  disabled,
}: AttachmentThumbnailsProps) {
  if (attachments.length === 0 && processingCount === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 px-3 pt-2">
      {attachments.map((att) =>
        att.kind === 'document' ? (
          <div
            key={att.id}
            className={cn(
              'group relative flex h-14 min-w-0 max-w-[11rem] shrink-0 items-center gap-2',
              'rounded-lg border border-[var(--border)] px-2 pr-6',
              'bg-[color-mix(in_oklch,var(--fg)_5%,transparent)]',
            )}
            title={`${att.name} (${formatBytes(att.byteSize)})`}
          >
            <FileText className="h-5 w-5 shrink-0 text-[var(--fg-muted)]" />
            <div className="min-w-0 leading-tight">
              <p className="truncate text-xs font-medium text-[var(--fg)]">{att.name}</p>
              <p className="text-[10px] text-[var(--fg-muted)]">
                {extensionLabel(att.name)} · {formatBytes(att.byteSize)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onRemove(att.id)}
              disabled={disabled}
              aria-label={`Remover ${att.name}`}
              className={cn(
                'absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full',
                'bg-black/60 text-white hover:bg-black/80 transition-colors disabled:opacity-50',
              )}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </div>
        ) : (
          <div
            key={att.id}
            className="group relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-[var(--border)]"
          >
            <img src={att.dataUrl} alt={att.name} className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => onRemove(att.id)}
              disabled={disabled}
              aria-label={`Remover ${att.name}`}
              className={cn(
                'absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full',
                'bg-black/60 text-white hover:bg-black/80 transition-colors disabled:opacity-50',
              )}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </div>
        ),
      )}
      {Array.from({ length: processingCount }).map((_, i) => (
        <div
          key={`processing-${i}`}
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]"
        >
          <Loader2 className="h-4 w-4 animate-spin text-[var(--fg-muted)]" />
        </div>
      ))}
    </div>
  );
}
